import type { TOC } from '@ember/component/template-only'

export interface RangeItem {
  bgColor: string
  showBorder?: boolean
  tooltip?: string
}

export interface AxisRangeItem {
  label?: string
  groupLabel?: string
  isLast: boolean
}

interface Signature {
  Args: {
    series: RangeItem[]
  }
  Element: HTMLDivElement
}

export const InlineSegmentedBar: TOC<Signature> = <template>
  <div data-test-inline-segmented-bar class="flex" ...attributes>
    {{#each @series as |row index|}}
      {{#if row.showBorder}}
        <div class="border-dusty-black-200 h-full border-l border-dashed" />
      {{/if}}
      <DataPoint data-test-inline-segmented-bar-data-point={{index}} @dataPoint={{row}} />
    {{/each}}
  </div>
</template>

interface DataSignature {
  Element: HTMLSpanElement
  Args: {
    dataPoint: RangeItem
  }
}

export const DataPoint: TOC<DataSignature> = <template>
  <span
    class="group {{@dataPoint.bgColor}} relative h-full grow border border-white hover:brightness-110"
    ...attributes
  >
    <Tooltip @dataPoint={{@dataPoint}} />
  </span>
</template>

interface SegmentedSignature {
  Args: {
    axisRange: AxisRangeItem[]
  }
  Element: HTMLDivElement
}
/**
 * This component renders an axis with labels and groupLabels.
 *
 * Given an array of AxisRangeItem
 *  - A label is rendered whenever label is defined.
 *  - A groupLabel is rendered whenever groupLabel is defined.
 *  - Last position is used to ensure the final label is correctly positioned.
 *
 * ```
 * interface AxisRangeItem {
 *   label?: string
 *   groupLabel?: string
 *   isLast: boolean
 * }
 * ```
 */
//
export const SegmentedAxis: TOC<SegmentedSignature> = <template>
  <div data-test-segmented-axis class="my-6 flex font-normal" ...attributes>
    {{#each @axisRange as |rangeItem|}}
      <span class="relative h-full grow border border-transparent" data-test-lesson={{rangeItem.label}}>
        {{#if rangeItem.groupLabel}}
          <span class="text-dusty-black-300 absolute -top-6 -left-0.5 whitespace-nowrap">
            {{! template-lint-disable no-bare-strings}}
            ▶
            {{rangeItem.groupLabel}}</span>
        {{/if}}
        {{#if rangeItem.label}}
          <span
            class="text-dusty-black-250 absolute z-10 text-sm {{if rangeItem.isLast 'right-0'}}"
          >{{rangeItem.label}}</span>
        {{/if}}
        <span></span>
      </span>
    {{/each}}
  </div>
</template>

interface TooltipSignature {
  Args: {
    dataPoint: RangeItem
  }
}

const Tooltip: TOC<TooltipSignature> = <template>
  {{#if @dataPoint.tooltip}}
    <div
      data-test-segmented-bar-tooltip
      class="z-tooltip absolute top-1/4 right-2 hidden w-auto bg-black px-4 py-2 whitespace-nowrap text-white
        {{if @dataPoint.tooltip 'group-hover:inline-block'}}"
    >
      {{@dataPoint.tooltip}}
    </div>
  {{/if}}
</template>
